import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar } from "./components/Navbar";
import { Loader } from "./components/UI";
import { ErrorBoundary } from "./components/ErrorBoundary";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PlayersList from "./pages/PlayersList";
import PlayerPassportPage from "./pages/PlayerPassportPage";
import ClubsList from "./pages/ClubsList";
import ClubPassportPage from "./pages/ClubPassportPage";
import FederationsList from "./pages/FederationsList";
import FederationPage from "./pages/FederationPage";
import CompetitionsList from "./pages/CompetitionsList";
import CompetitionPage from "./pages/CompetitionPage";
import MatchesList from "./pages/MatchesList";
import MatchPage from "./pages/MatchPage";
import LivePage from "./pages/LivePage";
import RankingsPage from "./pages/RankingsPage";
import CupPage from "./pages/CupPage";
import TiePage from "./pages/TiePage";
import NationsPage from "./pages/NationsPage";
import ChampionshipPage from "./pages/ChampionshipPage";
import ContinentalPage from "./pages/ContinentalPage";
import ContinentalTierPage from "./pages/ContinentalTierPage";
import Strike5Page from "./pages/Strike5Page";
import Strike5TournamentPage from "./pages/Strike5TournamentPage";
import ChallengePage from "./pages/ChallengePage";
import ChallengeTablePage from "./pages/ChallengeTablePage";
import HallOfFamePage from "./pages/HallOfFamePage";
import RivalryPage from "./pages/RivalryPage";
import RivalryIndexPage from "./pages/RivalryIndexPage";
import IntegrityPage from "./pages/IntegrityPage";
import NationalTeamPage from "./pages/NationalTeamPage";
import NationalTeamBuilder from "./pages/NationalTeamBuilder";
import CouponBuilder from "./pages/CouponBuilder";
import MatchControl from "./pages/MatchControl";
import Verify from "./pages/Verify";
import CertificateVerify from "./pages/CertificateVerify";
import PlayerDashboard from "./pages/PlayerDashboard";
import FedAdminDashboard from "./pages/FedAdminDashboard";
import WorldChampionshipPage from "./pages/WorldChampionshipPage";
import DuelTheatrePage from "./pages/DuelTheatrePage";
import SeasonRacePage from "./pages/SeasonRacePage";
import AdminSeasonsPage from "./pages/AdminSeasonsPage";
import SettingsPage from "./pages/SettingsPage";
import ShopPage from "./pages/ShopPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminIdMigration from "./pages/admin/AdminIdMigration";
import AdminClubRequests from "./pages/admin/AdminClubRequests";
import AdminNameChanges from "./pages/admin/AdminNameChanges";
import AdminCompetitions from "./pages/admin/AdminCompetitions";
import AdminEntryRequests from "./pages/admin/AdminEntryRequests";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCertificates from "./pages/admin/AdminCertificates";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminSportsbook from "./pages/admin/AdminSportsbook";
import SportsbookMethodologyPage from "./pages/SportsbookMethodologyPage";
import PartnerAuthorizeSim from "./pages/PartnerAuthorizeSim";
import ClubBuilder from "./pages/ClubBuilder";
import AboutPage from "./pages/AboutPage";
import AuthCallback from "./pages/AuthCallback";
import { Footer } from "./components/Footer";
import { LangProvider } from "./lib/i18n";
import { applyRouteMeta } from "./lib/seo";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  // Scroll to top on every navigation (not for same-path hash/query-only changes).
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  // Per-route SEO meta: title, description, canonical, OG/Twitter, robots.
  useEffect(() => { applyRouteMeta(location.pathname, location.search); }, [location.pathname, location.search]);
  // The super-admin panel carries its own topbar+sidebar chrome, so suppress the
  // global site Navbar on those routes (avoids a redundant double header).
  const isAdminPanel = /^\/admin(\/(audit-log|club-requests|name-change-requests|entry-requests|competitions(\/[^/]+)?|users|partners(\/[^/]+)?|sportsbook))?$/.test(location.pathname);
  // Development partner-authorize simulator: chrome-less (own layout, own banner).
  const isDevSim = /^\/dev\/partner-authorize\//.test(location.pathname);
  // Footer only on public/marketing pages — never on admin, app workspaces or embeds.
  const hideFooter = /^\/(admin|dashboard|club-builder|settings|dev|duel|embed|auth)(\/|$)|^\/match\/[^/]+\/(play|control)$/.test(location.pathname);
  return (
    <div className="min-h-screen flex flex-col bg-[#000000] text-slate-200">
      {!isAdminPanel && !isDevSim && <Navbar />}
      {/* Keyed wrapper replays a discreet fade-up on the incoming page only.
          Navbar sits outside so it never animates/flickers on navigation. */}
      <main key={location.pathname} className="page-transition flex-1">
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/players" element={<PlayersList />} />
          <Route path="/player/:wsbfId" element={<PlayerPassportPage />} />
          <Route path="/players/:wsbfId" element={<PlayerPassportPage />} />
          <Route path="/clubs" element={<ClubsList />} />
          <Route path="/club/:clubId" element={<ClubPassportPage />} />
          <Route path="/federations" element={<FederationsList />} />
          <Route path="/federation/:fedId" element={<FederationPage />} />
          <Route path="/competitions" element={<CompetitionsList />} />
          <Route path="/competition/:compId" element={<CompetitionPage />} />
          <Route path="/matches" element={<MatchesList />} />
          <Route path="/live" element={<LivePage />} />
          <Route path="/rankings" element={<RankingsPage />} />
          <Route path="/season-race" element={<SeasonRacePage />} />
          <Route path="/admin/seasons" element={<Protected><AdminSeasonsPage /></Protected>} />
          <Route path="/cup" element={<CupPage />} />
          <Route path="/cup/tie/:tieId" element={<TiePage />} />
          <Route path="/continental" element={<ContinentalPage />} />
          <Route path="/continental/:ident" element={<ContinentalTierPage />} />
          <Route path="/strike5" element={<Strike5Page />} />
          <Route path="/strike5/:id" element={<Strike5TournamentPage />} />
          <Route path="/challenge" element={<ChallengePage />} />
          <Route path="/challenge/:id" element={<ChallengeTablePage />} />
          <Route path="/hall-of-fame" element={<HallOfFamePage />} />
          <Route path="/rivalry" element={<RivalryIndexPage />} />
          <Route path="/rivalry/:type/:pair" element={<RivalryPage />} />
          <Route path="/integrity" element={<IntegrityPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/nations" element={<NationsPage />} />
          <Route path="/nations/championship" element={<ChampionshipPage />} />
          <Route path="/worlds" element={<ChampionshipPage variant="WORLDS" />} />
          <Route path="/world-championship" element={<WorldChampionshipPage />} />
          <Route path="/duel/:id" element={<DuelTheatrePage />} />
          <Route path="/nations/:country" element={<NationalTeamPage />} />
          <Route path="/admin/national-team" element={<Protected><NationalTeamBuilder /></Protected>} />
          <Route path="/match/:matchId" element={<MatchPage />} />
          <Route path="/match/:matchId/play" element={<Protected><CouponBuilder /></Protected>} />
          <Route path="/match/:matchId/control" element={<Protected><MatchControl /></Protected>} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/verify/:certNo" element={<CertificateVerify />} />
          <Route path="/verify/:type/:id" element={<Verify />} />
          <Route path="/dashboard" element={<Protected><PlayerDashboard /></Protected>} />
          <Route path="/dashboard/owner" element={<Navigate to="/club-builder" replace />} />
          <Route path="/club-builder" element={<Protected><ClubBuilder /></Protected>} />
          <Route path="/dashboard/club" element={<Navigate to="/club-builder" replace />} />
          <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/club-requests" element={<AdminClubRequests />} />
          <Route path="/admin/name-change-requests" element={<AdminNameChanges />} />
          <Route path="/admin/competitions" element={<AdminCompetitions />} />
          <Route path="/admin/competitions/:id" element={<AdminCompetitions />} />
          <Route path="/admin/entry-requests" element={<AdminEntryRequests />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/certificates" element={<AdminCertificates />} />
          <Route path="/admin/partners" element={<AdminPartners />} />
          <Route path="/admin/partners/:id" element={<AdminPartners />} />
          <Route path="/admin/sportsbook" element={<AdminSportsbook />} />
          <Route path="/admin/audit-log" element={<AdminAuditLog />} />
          <Route path="/admin/id-migration" element={<AdminIdMigration />} />
          <Route path="/sportsbook/methodology" element={<SportsbookMethodologyPage />} />
          <Route path="/dev/partner-authorize/:slug" element={<Protected><PartnerAuthorizeSim /></Protected>} />
          <Route path="/admin/federation" element={<Protected><FedAdminDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <ErrorBoundary>
            <Shell />
          </ErrorBoundary>
        </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}
